import {
  ApiCartChangeCartTypeFailedAction,
  ApiCartChangeCartTypeSucceededAction,
  apiCartChangeCartTypeFailedType,
  apiCartChangeCartTypeRequested,
  apiCartChangeCartTypeSucceededType
} from '../../api/cart/changeCartTypeApi';
import {
  ChangeCurrentCartTypeRequestedAction,
  ChangeDeliveryAddressPayload,
  ChangePaymentAddressPayload,
  changeCurrentCartDeliveryAddressRequested,
  changeCurrentCartPaymentAddressRequested,
  changeCurrentCartPaymentModeRequested,
  changeCurrentCartTypeFailed,
  changeCurrentCartTypeSucceeded
} from '../../carts/cartsSlice';
import { StateCart, StateCartPaymentMode } from '@spa/lib/store/carts/CartsState';
import { put, race, select, take } from 'typed-redux-saga';
import { ApiCartPaymentMode } from '../../api/cart/changeCartPaymentModeApi';
import { ChangeCartTypeResult } from '../../../graphql/cart/changeCartType';
import { CustomOrderType } from '../../carts/CustomOrderType';
import { TitleCode } from '@spa/lib/converters/convertCustomerTitles';
import convertCartToStateCart from '../../../converters/convertCartToStateCart';
import generateActionId from '../../../generateActionId';
import selectCarts from '@spa/lib/store/carts/selectors/selectCarts';
import selectGeneralSettings from '@spa/lib/store/settings/selectors/selectGeneralSettings';

const hasStaleCollectAtStorePaymentAddress = (cart: StateCart): boolean => {
  const deliveryAddress = cart.deliveryAddress;
  const paymentAddress = cart.paymentAddress;
  return (
    deliveryAddress?.storeCode !== null &&
    deliveryAddress?.storeCode !== undefined &&
    deliveryAddress.titleCode === paymentAddress?.titleCode &&
    deliveryAddress.firstName === paymentAddress?.firstName &&
    deliveryAddress.lastName === paymentAddress?.lastName &&
    deliveryAddress.line1 === paymentAddress?.line1 &&
    deliveryAddress.line2 === paymentAddress?.line2 &&
    deliveryAddress.additionalAddressInfo === paymentAddress?.additionalAddressInfo &&
    deliveryAddress.town === paymentAddress?.town &&
    deliveryAddress.postalCode === paymentAddress?.postalCode &&
    deliveryAddress.countryIsocode === paymentAddress?.countryIsocode
  );
};

const createResetPaymentAddress = (cart: StateCart): ChangePaymentAddressPayload | null => {
  const paymentAddress = cart.deliveryAddress;
  let resetPaymentAddress: ChangePaymentAddressPayload | null = null;
  if (paymentAddress !== null) {
    resetPaymentAddress = {
      titleCode: TitleCode.NOT_SPECIFIED,
      firstName: '',
      lastName: '',
      line1: '',
      line2: '',
      additionalAddressInfo: null,
      postalCode: '',
      town: '',
      countryIsocode:
        cart.paymentAddress?.countryIsocode ?? cart.deliveryAddress?.countryIsocode ?? '',
      email: cart.paymentAddress?.email ?? cart.deliveryAddress?.email ?? '',
      company: null
    };
  }
  return resetPaymentAddress;
};

const createResetDeliveryAddress = (cart: StateCart): ChangeDeliveryAddressPayload | null => {
  const deliveryAddress = cart.deliveryAddress;
  let resetDeliveryAddress: ChangeDeliveryAddressPayload | null = null;
  if (deliveryAddress !== null) {
    resetDeliveryAddress = {
      titleCode: TitleCode.NOT_SPECIFIED,
      firstName: '',
      lastName: '',
      line1: '',
      line2: '',
      additionalAddressInfo: null,
      postalCode: '',
      town: '',
      countryIsocode: deliveryAddress.countryIsocode,
      email: deliveryAddress.email,
      mobile: deliveryAddress.mobile,
      company: null
    };
  }
  return resetDeliveryAddress;
};

function* changeCurrentCartTypeSaga({
  payload: { actionId: requestedActionId, targetCustomOrderType }
}: ChangeCurrentCartTypeRequestedAction): Generator {
  const carts = yield* select(selectCarts);
  const { checkoutDeliverySelectionEnabled } = yield* select(selectGeneralSettings);
  const homeDeliveryCart = carts[CustomOrderType.HOME_DELIVERY];
  const collectAtStoreCart = carts[CustomOrderType.COLLECT_AT_STORE];
  const sourceCustomOrderType: CustomOrderType | null = homeDeliveryCart
    ? CustomOrderType.HOME_DELIVERY
    : collectAtStoreCart
      ? CustomOrderType.COLLECT_AT_STORE
      : null;
  const sourceCart = homeDeliveryCart ?? collectAtStoreCart ?? null;

  if (sourceCart === null || sourceCustomOrderType === null) {
    yield* put(changeCurrentCartTypeFailed({ requestedActionId }));
  } else if (sourceCustomOrderType === targetCustomOrderType) {
    yield* put(
      changeCurrentCartTypeSucceeded({
        cart: sourceCart,
        sourceCustomOrderType,
        targetCustomOrderType,
        requestedActionId
      })
    );
  } else {
    const actionId: number = generateActionId();
    yield* put(
      apiCartChangeCartTypeRequested({
        actionId,
        customOrderType: targetCustomOrderType,
        sourceCustomOrderType,
        parentActionId: requestedActionId
      })
    );

    yield* actionsLoop(
      actionId,
      requestedActionId,
      sourceCustomOrderType,
      targetCustomOrderType,
      checkoutDeliverySelectionEnabled
    );
  }
}

function* actionsLoop(
  actionId: number,
  requestedActionId: number,
  sourceCustomOrderType: CustomOrderType,
  targetCustomOrderType: CustomOrderType,
  checkoutDeliverySelectionEnabled: boolean
): Generator {
  let isSameActionId: boolean = false;
  do {
    const { succeeded, failed } = yield* race({
      succeeded: take<ApiCartChangeCartTypeSucceededAction>(apiCartChangeCartTypeSucceededType),
      failed: take<ApiCartChangeCartTypeFailedAction>(apiCartChangeCartTypeFailedType)
    });

    isSameActionId =
      succeeded?.payload.requestedActionId === actionId ||
      failed?.payload.requestedActionId === actionId;

    if (isSameActionId) {
      if (succeeded !== undefined) {
        const result: ChangeCartTypeResult = succeeded.payload.result;
        if (result.__typename === 'ChangeCartTypeFailedResult') {
          yield* put(
            changeCurrentCartTypeFailed({
              requestedActionId
            })
          );
        } else {
          const convertedCart = convertCartToStateCart(result.cart);

          yield* put(
            changeCurrentCartTypeSucceeded({
              cart: convertedCart,
              sourceCustomOrderType,
              targetCustomOrderType,
              requestedActionId
            })
          );

          if (
            sourceCustomOrderType === CustomOrderType.COLLECT_AT_STORE &&
            targetCustomOrderType === CustomOrderType.HOME_DELIVERY &&
            !checkoutDeliverySelectionEnabled &&
            convertedCart.deliveryAddress?.storeCode
          ) {
            const resetDeliveryAddress = createResetDeliveryAddress(convertedCart);
            if (resetDeliveryAddress !== null) {
              const resetDeliveryAddressActionId = generateActionId();
              yield* put(
                changeCurrentCartDeliveryAddressRequested({
                  actionId: resetDeliveryAddressActionId,
                  customOrderType: CustomOrderType.HOME_DELIVERY,
                  address: resetDeliveryAddress
                })
              );
            }
          }

          const paymentMode = convertedCart.payment?.mode ?? null;
          if (
            targetCustomOrderType === CustomOrderType.HOME_DELIVERY &&
            !checkoutDeliverySelectionEnabled &&
            hasStaleCollectAtStorePaymentAddress(convertedCart)
          ) {
            const resetPaymentAddress = createResetPaymentAddress(convertedCart);
            if (resetPaymentAddress !== null) {
              const resetPaymentAddressActionId = generateActionId();
              yield* put(
                changeCurrentCartPaymentAddressRequested({
                  actionId: resetPaymentAddressActionId,
                  customOrderType: CustomOrderType.HOME_DELIVERY,
                  address: resetPaymentAddress
                })
              );
            }
          }

          if (
            targetCustomOrderType === CustomOrderType.HOME_DELIVERY &&
            paymentMode === StateCartPaymentMode.PAY_AT_STORE
          ) {
            const paymentActionId = generateActionId();
            yield* put(
              changeCurrentCartPaymentModeRequested({
                actionId: paymentActionId,
                customOrderType: CustomOrderType.HOME_DELIVERY,
                paymentMode: ApiCartPaymentMode.NONE
              })
            );
          }
        }
      } else if (failed !== undefined) {
        yield* put(
          changeCurrentCartTypeFailed({
            requestedActionId
          })
        );
      }
    }
  } while (!isSameActionId);
}

export default changeCurrentCartTypeSaga;
